import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown as ChevronDownIcon, ChevronRight, Loader2, Search } from 'lucide-react';

import { Dropdown } from '@/components/Dropdown';
import { DatePicker } from '@/components/ui/DatePicker';

import type { ColumnDefinition, ColumnHeaderFilter, ColumnHeaderFilterConfig } from './types';
import { type DistinctValue, useDistinctValues } from './useDistinctValues';

/**
 * Popup filtra w nagłówku kolumny — port z SellCall (1:1 z drobnymi adaptacjami).
 *
 * Trzy zakładki w zależności od typu pola:
 *  - Wartości (zawsze, chyba że `filterConfig.conditionOnly`): lista checkboxów
 *    z unique values pobieranymi z backendu (przez `useDistinctValues`) lub
 *    statyczne `filterConfig.options` (dla pól typu select bez API fetch).
 *  - Zakres (number, date; nigdy przy `conditionOnly`): para input "Od / Do"
 *    z opcjonalnym dual-thumb slider'em (number) lub DatePickerami (date).
 *  - Warunek (text, number, date): operator + wartość ("zawiera"/"równe"/"większe niż"…).
 *
 * `filterConfig.conditionOnly === true` → tylko zakładka „Warunek" (bez „Wartości"
 * i „Zakres"), aktywna domyślnie, i `useDistinctValues` w ogóle się nie odpala —
 * dla kolumn liczonych przez silnik list, których pole nie jest na whiteliście
 * endpointu `/distinct` (patrz JSDoc `ColumnHeaderFilterConfig.conditionOnly`).
 *
 * Adaptacje vs SellCall:
 *  - DatePicker: ścieżka `@/components/ui/DatePicker` zamiast `../ui/DatePicker`
 *  - Etykiety zakładek (Wartości/Zakres/Warunek) idą przez `t('common.lists.filterTab.*')`.
 *    ZNANY DŁUG i18n (port z SellCall): etykiety operatorów (OPERATOR LABELS niżej) oraz
 *    klucze `common.apply/loading/value/search_placeholder` wciąż są hardcoded polskimi
 *    literałami — `check:i18n` ich nie łapie (skanuje braki kluczy t(), nie gołe literały).
 *  - Prop `moduleId` przemianowany na `tableId` dla spójności z resztą
 *    konfigurowalnej tabeli (`useTableLayout` też używa `tableId`).
 */

// ============================================================
// OPERATOR LABELS
// ============================================================

const TEXT_OPERATORS: Array<{ value: string; label: string }> = [
  { value: 'contains', label: 'Zawiera' },
  { value: 'not_contains', label: 'Nie zawiera' },
  { value: 'starts_with', label: 'Zaczyna się od' },
  { value: 'ends_with', label: 'Kończy się na' },
  { value: 'eq', label: 'Równe' },
  { value: 'neq', label: 'Nie równe' },
  { value: 'is_null', label: 'Jest puste' },
  { value: 'is_not_null', label: 'Nie jest puste' },
];

const NUMBER_OPERATORS: Array<{ value: string; label: string }> = [
  { value: 'eq', label: 'Równe' },
  { value: 'neq', label: 'Nie równe' },
  { value: 'gt', label: 'Większe niż' },
  { value: 'gte', label: 'Większe lub równe' },
  { value: 'lt', label: 'Mniejsze niż' },
  { value: 'lte', label: 'Mniejsze lub równe' },
  { value: 'is_null', label: 'Jest puste' },
  { value: 'is_not_null', label: 'Nie jest puste' },
];

const DATE_OPERATORS: Array<{ value: string; label: string }> = [
  { value: 'eq', label: 'Równe' },
  { value: 'gt', label: 'Po dacie' },
  { value: 'gte', label: 'Od daty' },
  { value: 'lt', label: 'Przed datą' },
  { value: 'lte', label: 'Do daty' },
  { value: 'is_null', label: 'Jest puste' },
  { value: 'is_not_null', label: 'Nie jest puste' },
];

const NO_VALUE_OPERATORS = ['is_null', 'is_not_null'];

function getOperatorsForCategory(category: string): Array<{ value: string; label: string }> {
  switch (category) {
    case 'number':
      return NUMBER_OPERATORS;
    case 'date':
      return DATE_OPERATORS;
    default:
      return TEXT_OPERATORS;
  }
}

// ============================================================
// PROPS
// ============================================================

interface ColumnHeaderFilterPopupProps {
  column: ColumnDefinition;
  filterConfig: ColumnHeaderFilterConfig;
  currentFilter: ColumnHeaderFilter | undefined;
  onApply: (filter: ColumnHeaderFilter | null) => void;
  onClose: () => void;
  anchorRect: { top: number; left: number; right: number; bottom: number };
  tableId?: string;
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function ColumnHeaderFilterPopup({
  column,
  filterConfig,
  currentFilter,
  onApply,
  onClose,
  anchorRect,
  tableId,
}: ColumnHeaderFilterPopupProps) {
  const { t } = useTranslation();
  const popupRef = useRef<HTMLDivElement>(null);
  const category = filterConfig.category;
  const conditionOnly = filterConfig.conditionOnly === true;
  const showConditionTab = category === 'text' || category === 'number' || category === 'date';
  const showRangeTab = !conditionOnly && (category === 'number' || category === 'date');
  const showValuesTab = !conditionOnly;

  const [activeTab, setActiveTab] = useState<'values' | 'condition' | 'range'>(
    conditionOnly
      ? 'condition'
      : currentFilter?.mode === 'condition'
        ? 'condition'
        : currentFilter?.mode === 'range'
          ? 'range'
          : 'values',
  );

  // Values tab state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedValues, setSelectedValues] = useState<Set<string | null>>(
    new Set(currentFilter?.mode === 'values' ? currentFilter.selectedValues || [] : []),
  );

  // Operatory dostępne w zakładce „Warunek" — przecięcie pełnej listy kategorii
  // (`getOperatorsForCategory`) z `filterConfig.operators`, gdy kolumna ogranicza zestaw
  // (np. `current_stage` → tylko `eq`: backend `buildStageFilter` zna dwa dopasowania etykiety —
  // dokładne i „zawiera” (to drugie wysyła wiersz filtrów); inne operatory tekstowe pominąłby
  // (filtr bez warunku), więc okno ich nie oferuje). Wstecznie kompatybilne: brak `operators` =
  // pełna lista dla kategorii, jak przed tą zmianą.
  const availableOperators = useMemo(() => {
    const all = getOperatorsForCategory(category);
    if (!filterConfig.operators || filterConfig.operators.length === 0) return all;
    const allowed = new Set(filterConfig.operators);
    return all.filter((op) => allowed.has(op.value));
  }, [category, filterConfig.operators]);

  // Lista operatorów w kontrolce „Operator": `availableOperators` + operator AKTYWNEGO filtra
  // kolumny, gdy zawężona lista go nie zawiera. Wiersz filtrów ustawia filtr operatorem spoza
  // zawężonej listy (np. „zawiera” na kolumnie `current_stage`, której okno oferuje tylko „Równe”),
  // a kontrolka (Radix) bez pozycji o aktywnej wartości pokazywała PUSTY warunek, choć filtr
  // działał tym operatorem („Zastosuj” wysyłał go bez zmian). Operator aktywnego filtra jest
  // więc widoczny jako bieżący wybór (z etykietą z listy kategorii); operatora nieznanego
  // kategorii nie dokładamy (surowy kod zamiast etykiety) — zostaje `availableOperators`;
  // żadnego innego operatora spoza zawężonej listy okno nie oferuje.
  const operatorOptions = useMemo(() => {
    const active = currentFilter?.operator;
    if (!active || availableOperators.some((op) => op.value === active)) return availableOperators;
    const known = getOperatorsForCategory(category).find((op) => op.value === active);
    return known ? [...availableOperators, known] : availableOperators;
  }, [availableOperators, category, currentFilter?.operator]);

  // Condition tab state
  const [operator, setOperator] = useState(() => {
    if (currentFilter?.operator) return currentFilter.operator;
    const naiveDefault = category === 'number' ? 'eq' : 'contains';
    // Gdy zestaw operatorów jest zawężony (`filterConfig.operators`) i domyślny operator
    // nie wchodzi w jego skład (np. `contains` przy `current_stage` -> tylko `eq`),
    // domyślny wybór spada na pierwszy dostępny — inaczej Dropdown (Radix) dostałby
    // `value` bez odpowiadającego `Item` i pokazywałby pusty trigger.
    return availableOperators.some((op) => op.value === naiveDefault)
      ? naiveDefault
      : (availableOperators[0]?.value ?? naiveDefault);
  });
  const [conditionValue, setConditionValue] = useState(currentFilter?.conditionValue || '');

  // Range tab state
  const [rangeMin, setRangeMin] = useState(currentFilter?.rangeMin || '');
  const [rangeMax, setRangeMax] = useState(currentFilter?.rangeMax || '');

  // Fetch distinct values — pominięte całkowicie przy `conditionOnly` (kolumny liczone
  // spoza whitelisty endpointu `/distinct` — inaczej 400 INVALID_FIELD, patrz JSDoc
  // `ColumnHeaderFilterConfig.conditionOnly`).
  const needsApiFetch =
    !conditionOnly && (!filterConfig.options || filterConfig.options.length === 0);
  const { data: distinctData, isLoading: distinctLoading } = useDistinctValues(
    tableId,
    filterConfig.fieldSlug,
    needsApiFetch && (activeTab === 'values' || activeTab === 'range'),
  );

  // Build values list
  const allValues = useMemo<DistinctValue[]>(() => {
    if (filterConfig.options && filterConfig.options.length > 0) {
      return [
        ...filterConfig.options.map((o) => ({
          value: o.value,
          label: o.label,
          count: 0,
          color: o.color,
        })),
        { value: null, label: t('common.lists.emptyValue'), count: 0 },
      ];
    }
    const raw = distinctData?.values || [];
    // Jeśli kolumna ma `transformLabel` (np. dla enumów z backendu),
    // mapuj label zachowując count i resztę pól.
    if (filterConfig.transformLabel) {
      const tl = filterConfig.transformLabel;
      return raw.map((v) => ({ ...v, label: tl(v.value) }));
    }
    return raw;
  }, [filterConfig.options, filterConfig.transformLabel, distinctData, t]);

  // Compute min/max for numeric range slider
  const numericBounds = useMemo(() => {
    if (category !== 'number') return null;
    let min = Infinity;
    let max = -Infinity;
    for (const v of allValues) {
      if (v.value === null) continue;
      const num = parseFloat(v.value);
      if (!isNaN(num)) {
        if (num < min) min = num;
        if (num > max) max = num;
      }
    }
    if (min === Infinity) return null;
    return { min, max };
  }, [category, allValues]);

  // Compute min/max for date range
  const dateBounds = useMemo(() => {
    if (category !== 'date') return null;
    let min = '';
    let max = '';
    for (const v of allValues) {
      if (v.value === null || !v.day) continue; // Only leaf date nodes
      if (!min || v.value < min) min = v.value;
      if (!max || v.value > max) max = v.value;
    }
    if (!min) return null;
    return { min, max };
  }, [category, allValues]);

  // Filter values by search term
  const filteredValues = useMemo(() => {
    if (!searchTerm) return allValues;
    const term = searchTerm.toLowerCase();
    return allValues.filter(
      (v) =>
        v.label.toLowerCase().includes(term) ||
        (v.value && String(v.value).toLowerCase().includes(term)),
    );
  }, [allValues, searchTerm]);

  // Date values — separate into tree structure
  const isDateField = category === 'date';
  const dateTree = useMemo(() => {
    if (!isDateField) return null;
    const years = new Map<number, { months: Map<number, DistinctValue[]>; value: DistinctValue }>();

    for (const v of allValues) {
      if (v.value === null) continue;
      if (v.year && !v.month && !v.day) {
        if (!years.has(v.year)) {
          years.set(v.year, { value: v, months: new Map() });
        } else {
          years.get(v.year)!.value = v;
        }
      }
      if (v.year && v.month && !v.day) {
        if (!years.has(v.year)) {
          years.set(v.year, {
            value: { ...v, label: String(v.year), count: 0 },
            months: new Map(),
          });
        }
        if (!years.get(v.year)!.months.has(v.month)) {
          years.get(v.year)!.months.set(v.month, []);
        }
      }
      if (v.year && v.month && v.day) {
        if (!years.has(v.year)) {
          years.set(v.year, {
            value: { ...v, label: String(v.year), count: 0 },
            months: new Map(),
          });
        }
        const yearEntry = years.get(v.year)!;
        if (!yearEntry.months.has(v.month)) {
          yearEntry.months.set(v.month, []);
        }
        yearEntry.months.get(v.month)!.push(v);
      }
    }
    return years;
  }, [isDateField, allValues]);

  // Positioning — anchorRect is relative to the ConfigurableTable root container
  const position = useMemo(() => {
    const popupWidth = 320;

    let left = anchorRect.left;
    const top = anchorRect.bottom + 4;

    // Overflow right — keep popup within view
    if (left + popupWidth > window.innerWidth - 16) {
      left = Math.max(0, anchorRect.right - popupWidth);
    }

    return { top, left };
  }, [anchorRect]);

  // Click outside
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popupRef.current && !popupRef.current.contains(target)) {
        // Panel opcji `Dropdown` (Radix) renderuje się w PORTALU poza drzewem
        // popupu — klik w opcję listy nie może zamykać popupu filtra.
        if (target instanceof Element && target.closest('[data-radix-popper-content-wrapper]')) {
          return;
        }
        onClose();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [onClose]);

  // Keyboard: Esc zamyka popup (komponent jest montowany tylko, gdy popup jest otwarty). Faza
  // przechwytywania + `preventDefault`: okno nakładkowe (`Overlay`), w którym stoi tabela, widzi
  // wtedy Esc jako już obsłużony i nie zamyka się razem z popupem.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [onClose]);

  const handleSelectAll = useCallback(() => {
    if (selectedValues.size === filteredValues.length) {
      setSelectedValues(new Set());
    } else {
      setSelectedValues(new Set(filteredValues.map((v) => v.value)));
    }
  }, [filteredValues, selectedValues]);

  const handleToggleValue = useCallback((value: string | null) => {
    setSelectedValues((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  }, []);

  const handleApply = () => {
    if (activeTab === 'values') {
      if (selectedValues.size === 0) {
        onApply(null);
      } else {
        onApply({
          field: column.id,
          mode: 'values',
          selectedValues: [...selectedValues],
        });
      }
    } else if (activeTab === 'range') {
      if (!rangeMin && !rangeMax) {
        onApply(null);
      } else {
        onApply({
          field: column.id,
          mode: 'range',
          rangeMin: rangeMin || undefined,
          rangeMax: rangeMax || undefined,
        });
      }
    } else {
      if (!operator || (NO_VALUE_OPERATORS.includes(operator) ? false : !conditionValue)) {
        if (NO_VALUE_OPERATORS.includes(operator)) {
          onApply({
            field: column.id,
            mode: 'condition',
            operator,
          });
        } else {
          onApply(null);
        }
      } else {
        onApply({
          field: column.id,
          mode: 'condition',
          operator,
          conditionValue,
        });
      }
    }
  };

  const handleClear = () => {
    onApply(null);
  };

  const allSelected = filteredValues.length > 0 && selectedValues.size === filteredValues.length;
  const someSelected = selectedValues.size > 0 && selectedValues.size < filteredValues.length;

  // Tab labels
  const tabs: Array<{ id: 'values' | 'condition' | 'range'; label: string }> = [];
  if (showValuesTab) {
    tabs.push({ id: 'values', label: t('common.lists.filterTab.values') });
  }
  if (showRangeTab) {
    tabs.push({ id: 'range', label: t('common.lists.filterTab.range') });
  }
  if (showConditionTab) {
    tabs.push({ id: 'condition', label: t('common.lists.filterTab.condition') });
  }

  return (
    <div
      ref={popupRef}
      className="absolute z-[9999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl"
      style={{
        top: position.top,
        left: position.left,
        width: 320,
        maxHeight: 480,
      }}
    >
      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-2 py-2.5 text-xs font-medium transition-colors ${
              activeTab === tab.id
                ? 'text-accent-600 border-b-2 border-accent-500 -mb-px'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ maxHeight: 380, overflowY: 'auto' }}>
        {/* ===== VALUES TAB ===== */}
        {activeTab === 'values' && (
          <div className="p-2">
            {/* Search */}
            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Szukaj..."
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-accent-500 focus:border-accent-500"
                autoFocus
              />
            </div>

            {/* Loading */}
            {distinctLoading && needsApiFetch && (
              <div className="flex items-center gap-2 py-4 justify-center text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-xs">Wczytywanie…</span>
              </div>
            )}

            {/* Values list */}
            {!distinctLoading && !isDateField && (
              <div className="space-y-0.5">
                {/* Select All */}
                <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-accent-600 focus:ring-accent-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                    (Zaznacz wszystkie)
                  </span>
                </label>

                {filteredValues.map((v, idx) => (
                  <label
                    key={v.value === null ? '__null__' : `${v.value}-${idx}`}
                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedValues.has(v.value)}
                      onChange={() => handleToggleValue(v.value)}
                      className="rounded border-gray-300 text-accent-600 focus:ring-accent-500"
                    />
                    <span className="flex-1 text-sm text-gray-700 dark:text-gray-300 truncate flex items-center gap-1.5">
                      {v.color && (
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: v.color }}
                        />
                      )}
                      {v.label}
                    </span>
                    {v.count > 0 && (
                      <span className="text-xs text-gray-400 flex-shrink-0">{v.count}</span>
                    )}
                  </label>
                ))}

                {filteredValues.length === 0 && !distinctLoading && (
                  <p className="text-xs text-gray-400 text-center py-3">Brak wartości</p>
                )}
              </div>
            )}

            {/* Date tree */}
            {!distinctLoading && isDateField && dateTree && (
              <DateTree
                dateTree={dateTree}
                selectedValues={selectedValues}
                onToggleValue={handleToggleValue}
                allValues={allValues}
              />
            )}
          </div>
        )}

        {/* ===== RANGE TAB ===== */}
        {activeTab === 'range' && category === 'number' && (
          <div className="p-3 space-y-4">
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Od
                </label>
                <input
                  type="number"
                  value={rangeMin}
                  onChange={(e) => setRangeMin(e.target.value)}
                  placeholder={numericBounds ? String(numericBounds.min) : 'Min'}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-accent-500"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleApply();
                  }}
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Do
                </label>
                <input
                  type="number"
                  value={rangeMax}
                  onChange={(e) => setRangeMax(e.target.value)}
                  placeholder={numericBounds ? String(numericBounds.max) : 'Max'}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-accent-500"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleApply();
                  }}
                />
              </div>
            </div>

            {/* Dual-thumb range slider */}
            {numericBounds && (
              <DualRangeSlider
                min={numericBounds.min}
                max={numericBounds.max}
                valueMin={rangeMin ? parseFloat(rangeMin) : numericBounds.min}
                valueMax={rangeMax ? parseFloat(rangeMax) : numericBounds.max}
                onChangeMin={(v) => setRangeMin(String(v))}
                onChangeMax={(v) => setRangeMax(String(v))}
              />
            )}

            {distinctLoading && needsApiFetch && (
              <div className="flex items-center gap-2 py-2 justify-center text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-xs">Ładowanie zakresu...</span>
              </div>
            )}
          </div>
        )}

        {activeTab === 'range' && category === 'date' && (
          <div className="p-3 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Od daty
              </label>
              <DatePicker
                value={rangeMin}
                onChange={(val) => setRangeMin(val)}
                minDate={dateBounds?.min}
                maxDate={rangeMax || dateBounds?.max}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Do daty
              </label>
              <DatePicker
                value={rangeMax}
                onChange={(val) => setRangeMax(val)}
                minDate={rangeMin || dateBounds?.min}
                maxDate={dateBounds?.max}
              />
            </div>

            {dateBounds && (
              <p className="text-xs text-gray-400">
                Dostępne dane: {dateBounds.min} — {dateBounds.max}
              </p>
            )}

            {distinctLoading && needsApiFetch && (
              <div className="flex items-center gap-2 py-2 justify-center text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-xs">Ładowanie zakresu...</span>
              </div>
            )}
          </div>
        )}

        {/* ===== CONDITION TAB ===== */}
        {activeTab === 'condition' && (
          <div className="p-3 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Operator
              </label>
              <Dropdown
                options={operatorOptions}
                value={operator}
                onChange={(val) => setOperator(val)}
                aria-label="Operator"
              />
            </div>

            {!NO_VALUE_OPERATORS.includes(operator) && (
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Wartość
                </label>
                <input
                  type={category === 'number' ? 'number' : category === 'date' ? 'date' : 'text'}
                  value={conditionValue}
                  onChange={(e) => setConditionValue(e.target.value)}
                  placeholder="Wpisz wartość..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-accent-500"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleApply();
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-3 py-2.5 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={handleClear}
          className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          Wyczyść filtr
        </button>
        <button
          type="button"
          onClick={handleApply}
          className="px-4 py-1.5 text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 rounded transition-colors"
        >
          Zastosuj
        </button>
      </div>
    </div>
  );
}

// ============================================================
// DUAL RANGE SLIDER COMPONENT
// ============================================================

interface DualRangeSliderProps {
  min: number;
  max: number;
  valueMin: number;
  valueMax: number;
  onChangeMin: (value: number) => void;
  onChangeMax: (value: number) => void;
}

function DualRangeSlider({
  min,
  max,
  valueMin,
  valueMax,
  onChangeMin,
  onChangeMax,
}: DualRangeSliderProps) {
  const trackRef = useRef<HTMLDivElement>(null);

  const range = max - min || 1;
  const leftPercent = Math.max(0, Math.min(100, ((valueMin - min) / range) * 100));
  const rightPercent = Math.max(0, Math.min(100, ((valueMax - min) / range) * 100));

  const handleMouseDown = (thumb: 'min' | 'max') => (e: React.MouseEvent) => {
    e.preventDefault();
    const track = trackRef.current;
    if (!track) return;

    const onMouseMove = (me: MouseEvent) => {
      const rect = track.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (me.clientX - rect.left) / rect.width));
      const rawValue = min + percent * range;
      // Round to 2 decimals
      const value = Math.round(rawValue * 100) / 100;

      if (thumb === 'min') {
        onChangeMin(Math.min(value, valueMax));
      } else {
        onChangeMax(Math.max(value, valueMin));
      }
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  return (
    <div className="px-1 py-2">
      <div ref={trackRef} className="relative h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full">
        {/* Active range */}
        <div
          className="absolute h-full bg-accent-500 rounded-full"
          style={{ left: `${leftPercent}%`, width: `${rightPercent - leftPercent}%` }}
        />

        {/* Min thumb */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-accent-500 rounded-full cursor-pointer shadow hover:scale-110 transition-transform"
          style={{ left: `${leftPercent}%`, marginLeft: -8 }}
          onMouseDown={handleMouseDown('min')}
        />

        {/* Max thumb */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-accent-500 rounded-full cursor-pointer shadow hover:scale-110 transition-transform"
          style={{ left: `${rightPercent}%`, marginLeft: -8 }}
          onMouseDown={handleMouseDown('max')}
        />
      </div>
    </div>
  );
}

// ============================================================
// DATE TREE COMPONENT
// ============================================================

interface DateTreeProps {
  dateTree: Map<number, { months: Map<number, DistinctValue[]>; value: DistinctValue }>;
  selectedValues: Set<string | null>;
  onToggleValue: (value: string | null) => void;
  allValues: DistinctValue[];
}

function DateTree({ dateTree, selectedValues, onToggleValue, allValues }: DateTreeProps) {
  const { t } = useTranslation();
  const [expandedYears, setExpandedYears] = useState<Set<number>>(new Set());
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());

  const toggleYear = (year: number) => {
    setExpandedYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });
  };

  const toggleMonth = (key: string) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleToggleYear = (year: number) => {
    const yearEntry = dateTree.get(year);
    if (!yearEntry) return;

    const dayValues: string[] = [];
    yearEntry.months.forEach((days) => {
      days.forEach((d) => {
        if (d.value) dayValues.push(d.value);
      });
    });

    const allSelected = dayValues.every((v) => selectedValues.has(v));
    dayValues.forEach((v) => {
      if (allSelected) {
        if (selectedValues.has(v)) onToggleValue(v);
      } else {
        if (!selectedValues.has(v)) onToggleValue(v);
      }
    });
  };

  const handleToggleMonth = (year: number, month: number) => {
    const yearEntry = dateTree.get(year);
    if (!yearEntry) return;
    const days = yearEntry.months.get(month) || [];
    const dayValues = days.map((d) => d.value).filter((v): v is string => v !== null);

    const allSelected = dayValues.every((v) => selectedValues.has(v));
    dayValues.forEach((v) => {
      if (allSelected) {
        if (selectedValues.has(v)) onToggleValue(v);
      } else {
        if (!selectedValues.has(v)) onToggleValue(v);
      }
    });
  };

  const nullEntry = allValues.find((v) => v.value === null);
  const sortedYears = [...dateTree.keys()].sort((a, b) => b - a);

  return (
    <div className="space-y-0.5">
      {nullEntry && (
        <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={selectedValues.has(null)}
            onChange={() => onToggleValue(null)}
            className="rounded border-gray-300 text-accent-600 focus:ring-accent-500"
          />
          <span className="text-sm text-gray-500 italic">{t('common.lists.emptyValue')}</span>
          {nullEntry.count > 0 && <span className="text-xs text-gray-400">{nullEntry.count}</span>}
        </label>
      )}

      {sortedYears.map((year) => {
        const yearEntry = dateTree.get(year)!;
        const isExpanded = expandedYears.has(year);
        const monthKeys = [...yearEntry.months.keys()].sort((a, b) => a - b);

        return (
          <div key={year}>
            <div className="flex items-center gap-1 px-1 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700">
              <button
                type="button"
                onClick={() => toggleYear(year)}
                className="p-0.5 text-gray-400"
              >
                {isExpanded ? (
                  <ChevronDownIcon className="w-3.5 h-3.5" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5" />
                )}
              </button>
              <label className="flex items-center gap-2 flex-1 cursor-pointer">
                <input
                  type="checkbox"
                  onChange={() => handleToggleYear(year)}
                  className="rounded border-gray-300 text-accent-600 focus:ring-accent-500"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{year}</span>
                <span className="text-xs text-gray-400">{yearEntry.value.count}</span>
              </label>
            </div>

            {isExpanded &&
              monthKeys.map((month) => {
                const days = yearEntry.months.get(month) || [];
                const monthKey = `${year}-${month}`;
                const isMonthExpanded = expandedMonths.has(monthKey);
                const monthLabel = new Date(year, month - 1).toLocaleString('pl-PL', {
                  month: 'long',
                });

                return (
                  <div key={monthKey} className="ml-5">
                    <div className="flex items-center gap-1 px-1 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700">
                      <button
                        type="button"
                        onClick={() => toggleMonth(monthKey)}
                        className="p-0.5 text-gray-400"
                      >
                        {isMonthExpanded ? (
                          <ChevronDownIcon className="w-3.5 h-3.5" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5" />
                        )}
                      </button>
                      <label className="flex items-center gap-2 flex-1 cursor-pointer">
                        <input
                          type="checkbox"
                          onChange={() => handleToggleMonth(year, month)}
                          className="rounded border-gray-300 text-accent-600 focus:ring-accent-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1)}
                        </span>
                        <span className="text-xs text-gray-400">{days.length}</span>
                      </label>
                    </div>

                    {isMonthExpanded &&
                      days.map((day) => (
                        <label
                          key={day.value}
                          className="flex items-center gap-2 ml-6 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedValues.has(day.value)}
                            onChange={() => onToggleValue(day.value)}
                            className="rounded border-gray-300 text-accent-600 focus:ring-accent-500"
                          />
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {day.label}
                          </span>
                          {day.count > 0 && (
                            <span className="text-xs text-gray-400">{day.count}</span>
                          )}
                        </label>
                      ))}
                  </div>
                );
              })}
          </div>
        );
      })}
    </div>
  );
}
